/**
 * Add ticker — the list page's header button and its dialog.
 *
 * Ported from views/list.ejs and its client script: `#add-ticker-header-btn` opens `#add-ticker-modal`, which
 * has the symbol field `#at_symbol` with suggestions from `/api/v1/ticker-search` and the submit
 * `#add-ticker-submit`. A ticker with no transaction yet is a WATCHLIST entry, so the write is
 * `POST /api/v1/lists/:id/watchlist` — the same service call the EJS form makes.
 *
 * Escape has two consumers while the suggestion list is open: the first press closes the suggestions and only
 * the second closes the dialog. The EJS page learned that the hard way (an unconditional preventDefault made
 * the dialog unclosable by keyboard), so the ordering is deliberate here too.
 */
import { useEffect, useRef, useState } from 'react';
import { Button } from '../components/Button';
import { Modal } from '../components/Modal';
import { registerModal, unregisterModal } from '../shell/pfmodal';

type Suggest = { symbol: string; name?: string };

export function AddTicker({ listId, onAdded }: { listId: number; onAdded: () => void }) {
  const [open, setOpen] = useState(false);
  const [symbol, setSymbol] = useState('');
  const [local, setLocal] = useState<Suggest[]>([]);
  const [yahoo, setYahoo] = useState<Suggest[]>([]);
  const [showSuggest, setShowSuggest] = useState(false);
  // Escape closes the list, and the dialog re-focuses this field on every render — so focus alone must not
  // re-open what the reader just dismissed. Typing clears the flag; focus respects it.
  const [dismissed, setDismissed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const input = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    registerModal('add-ticker-modal', { open: () => setOpen(true), close: () => setOpen(false) });
    return () => unregisterModal('add-ticker-modal');
  }, []);

  // First Escape closes the suggestion list wherever the focus is. The window-capture listener in Modal runs
  // before this one and skips while data-esc-open is present, so this is the handler that consumes that press.
  useEffect(() => {
    if (!open || !showSuggest) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      setShowSuggest(false);
      setDismissed(true);
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [open, showSuggest]);

  // Debounced suggestions: 250ms, and the search's own failures are silent (an empty list is a fine answer).
  useEffect(() => {
    const q = symbol.trim();
    if (!open || q.length < 2) {
      setLocal([]);
      setYahoo([]);
      return;
    }
    const t = setTimeout(async () => {
      try {
        const res = await fetch(`/api/v1/ticker-search?q=${encodeURIComponent(q)}`, { credentials: 'same-origin' });
        const json = await res.json();
        const data = json?.data || { local: [], yahoo: [] };
        setLocal(data.local || []);
        setYahoo(data.yahoo || []);
      } catch {
        setLocal([]);
        setYahoo([]);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [symbol, open]);

  const submit = async (ticker?: string) => {
    const t = (ticker ?? symbol).trim().toUpperCase();
    if (!t) return;
    setBusy(true);
    setError(null);
    try {
      const res = await fetch(`/api/v1/lists/${listId}/watchlist`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ticker: t }),
      });
      // A 2xx is the answer. Reading the body first made an empty one look like a failure: some routes reply
      // 200 with no JSON, and the row appeared anyway — while the dialog reported an error.
      if (!res.ok) {
        const json = await res.json().catch(() => null);
        throw new Error(json?.error || `HTTP ${res.status}`);
      }
      setOpen(false);
      setSymbol('');
      onAdded();
      // A real navigation, as the EJS form's POST is: the reader sees the new row, and a test that waits for
      // 'load' is waiting for "added", not for "the request went out".
      window.location.assign(`/lists/${listId}`);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const suggestions = [...local, ...yahoo];
  const closeTop = () => {
    if (!showSuggest) return false;
    setShowSuggest(false);
    return true;
  };

  return (
    <>
      <button
        type="button"
        id="add-ticker-header-btn"
        // views/list.ejs writes the component's classes first: `btn secondary js-add-ticker-open t-body`.
        className="btn secondary js-add-ticker-open t-body"
        onClick={() => {
          setOpen(true);
          setTimeout(() => input.current?.focus(), 0);
        }}
      >
        + Add ticker
      </button>

      <Modal id="add-ticker-modal" open={open} onClose={() => setOpen(false)} title="Add ticker" size="sm">
        {error ? <div className="error" style={{ marginBottom: 8 }}>{error}</div> : null}
        {/* The body is a <form id="add-ticker-form">, as views/partials/modals/add-ticker-body.ejs writes it — the
            element the page's own checks address and the thing that makes Enter submit. Its field is the shared
            form-field component: a div.field holding a <label for> and the control, with the red asterisk. */}
        <form
          id="add-ticker-form"
          onSubmit={(e) => {
            e.preventDefault();
            void submit();
          }}
        >
        {/* The field and its suggestion list live in a positioned wrapper, as the EJS partial has them: the
            list is an overlay under the input, not a block that pushes the actions down while it is open. */}
        <div style={{ position: 'relative' }}>
        <div className="field">
          <label htmlFor="at_symbol">
            Ticker symbol <span className="req">*</span>
          </label>
          <input
            id="at_symbol"
            ref={input}
            name="ticker"
            value={symbol}
            autoComplete="off"
            onChange={(e) => {
              setSymbol(e.target.value.toUpperCase());
              setDismissed(false);
              setShowSuggest(true);
            }}
            onFocus={() => {
              if (!dismissed && symbol.trim().length >= 2) setShowSuggest(true);
            }}
            onKeyDown={(e) => {
              // First Escape closes the suggestions, second closes the dialog — see the note at the top.
              if (e.key === 'Escape' && closeTop()) {
                e.preventDefault();
                e.stopPropagation();
              }
              if (e.key === 'Enter') {
                e.preventDefault();
                void submit();
              }
            }}
            placeholder="AAPL"
          />
        </div>
        </div>
        {showSuggest && suggestions.length > 0 ? (
          <div
            id="at-suggest"
            data-esc-open="1"
            style={{
              position: 'absolute',
              left: 0,
              right: 0,
              top: '100%',
              zIndex: 50,
              marginTop: 6,
              maxHeight: 240,
              overflowY: 'auto',
              background: 'var(--panel)',
              border: '1px solid var(--border)',
              borderRadius: 8,
              boxShadow: '0 8px 24px var(--scrim)',
            }}
          >
            {suggestions.map((s) => (
              <button
                key={s.symbol}
                type="button"
                className="sel-opt sug-item"
                onClick={() => {
                  setSymbol(s.symbol);
                  setShowSuggest(false);
                  void submit(s.symbol);
                }}
              >
                <strong>{s.symbol}</strong>
                {s.name ? <span className="muted t-small"> {s.name}</span> : null}
              </button>
            ))}
          </div>
        ) : null}
        {/* Both actions are ghost buttons on the EJS row — Cancel quiet, Add in the accent text — not a filled
            secondary/primary pair, which read as a different dialog. */}
        <div className="modal-actions" style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 14 }}>
          <Button id="add-ticker-cancel" variant="ghost" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button id="add-ticker-submit" variant="ghost" className="btn-accent-text" disabled={busy} onClick={() => void submit()}>
            {busy ? 'Adding…' : 'Add'}
          </Button>
        </div>
        </form>
      </Modal>
    </>
  );
}
